/**
 * Purpose: Provides a single point of entry for the application to
 * obtain a new access token.
 *
 * It has no context of who is asking for a refresh, while only cares about:
 * - there is only one concurrent request
 * - if the server rejects the refresh, it assumes the refresh token has expired (and we log the user out)
 * - a network failure or server error keeps the session, since the refresh token may still be valid
 *
 * Resolves `true` when a new token was obtained and `false` otherwise, so
 * callers can skip retrying requests.
 */
import axios, {
  isAxiosError,
  type AxiosError,
  type AxiosResponse,
} from 'axios';
import { jwtDecode } from 'jwt-decode';

import AuthenticationStore, {
  getAuthSnapshot,
} from '~/auth/authenticationStore';
import { LOGIN_CONSTANTS } from '~/common/constants/login';
import { getClientFromUrl } from '~/routing/utils';
import Configuration from '~/services/configuration';
import {
  redirectToDomainLogin,
  redirectToLogin,
  redirectToLoginEmbed,
} from '~/services/redirects';
import { dedupeWhilePending } from '~/utils/function';
import { logger } from '~/utils/logger';
import {
  captureMessage,
  countForcedLogout,
  toSentryTags,
} from '~/utils/sentry';

interface AegisRefreshResponse {
  access_token: string;
}

function getRefreshAccessToken(
  refreshToken: string,
): Promise<AxiosResponse<AegisRefreshResponse>> {
  const AS = AuthenticationStore();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${refreshToken}`,
  };
  const csrf = AS.getCsrf();
  if (csrf) {
    headers[LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN] = csrf;
  }

  return axios.post<AegisRefreshResponse>(
    `${Configuration.authEndpoint}/v1/auth/refresh`,
    {},
    { headers },
  );
}

function getRefreshAccessHttpOnlyToken(): Promise<AxiosResponse<unknown>> {
  const AS = AuthenticationStore();
  const csrf = AS.getCsrf();

  return axios.post(
    `${Configuration.authEndpoint}/v1/auth/refresh`,
    {},
    {
      headers: csrf ? { [LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN]: csrf } : {},
      withCredentials: true,
    },
  );
}

function handleRefreshFailure(error: unknown) {
  const AS = AuthenticationStore();

  const details = {
    reason: 'refresh_token_failure',
    subdomainOnTab: getClientFromUrl(),
    subdomainInSession: AS.getSubDomainInSession(),
    hadRefreshToken: Boolean(AS.getRefreshToken()),
    hadCsrf: Boolean(AS.getCsrf()),
    status: isAxiosError(error) ? error.response?.status : undefined,
    responseBody: isAxiosError(error) ? error.response?.data : undefined,
  };
  logger.auth('Forced logout: access token refresh failed %o', details);
  countForcedLogout('token_refresh_failed');
  AS.clearAuthentication({ requiresReauth: true });
}

function redirectToSessionLogin() {
  const subdomain = getClientFromUrl();

  if (!subdomain) {
    redirectToDomainLogin();
    return;
  }
  if (AuthenticationStore().isEmbed()) {
    redirectToLoginEmbed();
    return;
  }
  redirectToLogin(subdomain);
}

function handleRolodexRefreshFailure(error: unknown) {
  // Clear without `requiresReauth` so the re-auth modal does not arm and
  // block the redirect; SSO users have no in-tab re-auth path.
  const AS = AuthenticationStore();

  const details = {
    reason: 'rolodex_refresh_failure',
    subdomainOnTab: getClientFromUrl(),
    subdomainInSession: AS.getSubDomainInSession(),
    hadCsrf: Boolean(AS.getCsrf()),
    status: isAxiosError(error) ? error.response?.status : undefined,
    responseBody: isAxiosError(error) ? error.response?.data : undefined,
  };
  logger.auth('Forced logout: rolodex session refresh failed %o', details);
  countForcedLogout('rolodex_refresh_failed');
  AS.clearAuthentication();
  redirectToSessionLogin();
}

function isTransientRefreshFailure(error: unknown): error is AxiosError {
  if (!isAxiosError(error)) return false;

  const status = error.response?.status;
  return status === undefined || status >= 500;
}

function reportTransientRefreshFailure(error: AxiosError, isRolodex: boolean) {
  const details = {
    reason: 'refresh_transient_failure',
    isRolodexAuthed: isRolodex,
    status: error.response?.status,
    code: error.code,
  };
  logger.auth('Token refresh failed, session kept %o', details);
  captureMessage('Token refresh failed: session kept', toSentryTags(details));
}

async function refreshAegisAccessToken() {
  const AS = AuthenticationStore();
  const refreshToken = AS.getRefreshToken();

  if (refreshToken) {
    // when httponly flag is not enabled
    const response = await getRefreshAccessToken(refreshToken);

    const accessToken = response.data.access_token;
    AS.saveAccessToken(accessToken);
    return;
  }

  // when httponly flag is enabled
  await getRefreshAccessHttpOnlyToken();
}

function refreshRolodexSessionJwt() {
  const csrf = AuthenticationStore().getCsrf();

  return axios.post(
    `${Configuration.rolodexEndpoint}/api/v1/token/refresh`,
    {},
    {
      headers: csrf ? { [LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN]: csrf } : {},
      withCredentials: true,
    },
  );
}

function endStaleSession() {
  const snapshot = getAuthSnapshot();
  if (!snapshot.isLoggedIn) return;

  logger.auth('Skipped token refresh: session already ended');

  if (snapshot.isRolodexAuthed) {
    AuthenticationStore().clearAuthentication();
    redirectToSessionLogin();
    return;
  }

  AuthenticationStore().clearAuthentication({ requiresReauth: true });
}

async function refreshSession() {
  const AS = AuthenticationStore();

  if (AS.requiresReauth()) return false;

  if (!AS.isLoggedIn()) {
    endStaleSession();
    return false;
  }

  const isRolodex = AS.isRolodexAuthed();
  const refresh = isRolodex
    ? refreshRolodexSessionJwt
    : refreshAegisAccessToken;

  try {
    await refresh();
    AS.saveJwtTokenCreationTime();
    return true;
  } catch (error) {
    if (isTransientRefreshFailure(error)) {
      reportTransientRefreshFailure(error, isRolodex);
    } else if (isRolodex) {
      handleRolodexRefreshFailure(error);
    } else {
      handleRefreshFailure(error);
    }
    return false;
  }
}

export const refreshAccessToken = dedupeWhilePending(refreshSession);

export const shouldRefreshAccessToken = () => {
  const AS = AuthenticationStore();
  const accessToken = AS.getAccessToken();
  const accessTokenCreationTime =
    // creation date + 15 minutes if Aegis, 5 minutes if Rolodex. The
    // `?? 0` mirrors the original JS `null + number === number` coercion
    // for the case where no creation time was ever recorded.
    (AS.getAccessTokenCreationTime() ?? 0) +
    (AS.isRolodexAuthed() ? 300000 : 900000);

  const tokenExpTimestamp = accessToken
    ? jwtDecode<{ exp: number }>(accessToken).exp * 1000
    : accessTokenCreationTime;

  // TODO: remove this check and the ternary above? Or return true?
  // It is unclear how this can happen without manually fiddling with the cookie in the browser console,
  // the addition of this IF block is to call this out explicitly without making actual changes.
  if (!tokenExpTimestamp) {
    return false;
  }

  const now = Date.now();
  const expiration = tokenExpTimestamp;

  if (now > expiration) {
    return true;
  }

  if (AS.isRolodexAuthed()) {
    // 1000 * 60 is the buffer for some users browser time being minute(s) off and
    // tokenExpTimestamp is server-issued - better renew the token sooner than later
    const oneMinuteFromNow = now + 1000 * 60;
    if (oneMinuteFromNow > expiration) {
      return true;
    }
  } else {
    // 1000 * 60 * 5 is the buffer for some users browser time being minute(s) off and
    // tokenExpTimestamp is server-issued - better renew the token sooner than later
    const fiveMinutesFromNow = now + 1000 * 60 * 5;
    if (fiveMinutesFromNow > expiration) {
      return true;
    }
  }

  return false;
};
